import api from "./api";

// Helper function to prepare patient data for backend
const preparePatientData = (formData) => {
  // Helper to convert empty string to null (BUT keep empty arrays!)
  const cleanValue = (value) => {
    if (value === undefined) return null;
    // ✅ KEEP empty strings for text fields - backend can handle them
    if (typeof value === "string") {
      const trimmed = value.trim();
      // Return empty string if that's what it is, don't convert to null
      // Backend will handle validation
      return trimmed;
    }
    return value;
  };

  // Helper to convert string to array (for multiline text fields)
  const stringToArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    // Split by newlines, trim each item, filter out empty strings
    return value.split('\n').map(item => item.trim()).filter(item => item.length > 0);
  };

  const cleaned = {
    // Basic Info
    firstName: cleanValue(formData.firstName),
    lastName: cleanValue(formData.lastName),
    middleName: cleanValue(formData.middleName),
    email: cleanValue(formData.email),
    
    // ✅ FIX: Map 'phone' → 'phoneNumber'
    phoneNumber: cleanValue(formData.phone || formData.phoneNumber),
    
    dateOfBirth: cleanValue(formData.dateOfBirth),
    gender: cleanValue(formData.gender),
    bloodType: cleanValue(formData.bloodType),
    userId: cleanValue(formData.userId),

    // Address fields
    // ✅ FIX: Map 'address' → 'streetAddress'
    streetAddress: cleanValue(formData.address || formData.streetAddress),
    city: cleanValue(formData.city),
    state: cleanValue(formData.state),
    zipCode: cleanValue(formData.zipCode),
    country: cleanValue(formData.country) || "USA",

    // Physical measurements
    heightCm: formData.heightCm ? parseFloat(formData.heightCm) : null,
    weightKg: formData.weightKg ? parseFloat(formData.weightKg) : null,

    // Medical Info
    diagnosisDate: cleanValue(formData.diagnosisDate),
    ckdStage: cleanValue(formData.ckdStage),
    ckdEtiology: cleanValue(formData.ckdEtiology),
    egfr: formData.egfr ? parseFloat(formData.egfr) : null,
    performanceStatus: cleanValue(formData.performanceStatus),

    // Dialysis status
    dialysisModality: cleanValue(formData.dialysisModality),
    
    // ✅ FIX: Convert string to array for allergies
    allergies: stringToArray(formData.allergies),
    
    // ✅ FIX: Map 'otherConditions' → 'medicalConditions' and convert to array
    medicalConditions: stringToArray(formData.otherConditions || formData.medicalConditions),
    
    // ✅ FIX: Convert string to array for currentMedications
    currentMedications: stringToArray(formData.currentMedications),

    // Emergency Contact
    emergencyContactName: cleanValue(formData.emergencyContactName),
    emergencyContactRelationship: cleanValue(formData.emergencyContactRelationship),
    emergencyContactPhone: cleanValue(formData.emergencyContactPhone),

    // Healthcare providers
    primaryPhysicianName: cleanValue(formData.primaryPhysicianName),
    primaryPhysicianPhone: cleanValue(formData.primaryPhysicianPhone),
    treatingInstitution: cleanValue(formData.treatingInstitution),

    // Trial preferences
    willingToTravel: Boolean(formData.willingToTravel),
    maxTravelDistanceKm: formData.maxTravelDistanceKm
      ? parseInt(formData.maxTravelDistanceKm)
      : null,
    interestedInTrials: Boolean(formData.interestedInTrials),

    // Notes
    notes: cleanValue(formData.notes),

    // Consent - ALWAYS include booleans, never remove them
    hipaaConsent: Boolean(formData.hipaaConsent),
    termsAccepted: Boolean(formData.termsAccepted),
    privacyPolicyAccepted: Boolean(formData.privacyPolicyAccepted),
    researchConsent: Boolean(formData.researchConsent),
    dataSharingConsent: Boolean(formData.dataSharingConsent),
  };

  // ✅ FIX: Keep empty strings and empty arrays - backend needs them to clear fields
  // Only remove null/undefined, keep everything else including:
  // - Empty strings ""
  // - Empty arrays []
  // - Boolean false
  // - Number 0
  Object.keys(cleaned).forEach((key) => {
    const value = cleaned[key];
    // Only remove if null or undefined
    if (value === null || value === undefined) {
      delete cleaned[key];
    }
  });

  console.log("🧹 Cleaned patient data:", cleaned);

  return cleaned;
};

// Helper to parse backend validation errors
const parseBackendErrors = (errorResponse) => {
  const fieldErrors = {};

  if (errorResponse?.error && typeof errorResponse.error === "string") {
    const errorText = errorResponse.error;

    // Parse Spring Boot validation errors
    const fieldErrorPattern =
      /Field error in object '.*?' on field '(.*?)':.*?default message \[(.*?)\]/g;
    let match;

    while ((match = fieldErrorPattern.exec(errorText)) !== null) {
      const fieldName = match[1];
      const message = match[2];
      fieldErrors[fieldName] = message;
    }
  }

  return fieldErrors;
};

const patientService = {
  getPatientById: async (patientId) => {
    try {
      const endpoint =
        patientId === "me" ? "/v1/patients/me" : `/v1/patients/${patientId}`;
      const response = await api.get(endpoint);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching patient:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: null,
      };
    }
  },

  getMyProfile: async () => {
    try {
      const response = await api.get("/v1/patients/me");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching my profile:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: null,
      };
    }
  },

  // Idempotent: safe to call even if a profile already exists (returns
  // the existing one unchanged). Used to make sure a Patient record
  // exists before sending a newly-authenticated user to the profile
  // setup page — covers social login and Auth0-hosted email/password
  // signup, neither of which create a Patient record on their own.
  initializeProfile: async ({ email, firstName, lastName } = {}) => {
    try {
      const response = await api.post("/v1/patients/me/initialize", {
        email,
        firstName,
        lastName,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error initializing profile:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: null,
      };
    }
  },

  createPatient: async (patientData) => {
    try {
      const { userId, ...dataWithoutUserId } = patientData;

      const cleanData = preparePatientData(dataWithoutUserId);
      console.log("📤 Creating NEW patient (no userId in payload):", cleanData);

      const response = await api.post("/v1/patients", cleanData);

      return {
        success: true,
        data: response.data,
        message: "Patient profile created successfully",
      };
    } catch (error) {
      console.error("❌ Error creating patient:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Request URL:", error.config?.url);

      const fieldErrors = parseBackendErrors(error.response?.data);

      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to create patient profile",
        fieldErrors,
      };
    }
  },

  updatePatient: async (patientId, patientData) => {
    try {
      if (!patientId || patientId === "undefined" || patientId === "null") {
        console.error("❌ Invalid patientId:", patientId);
        return {
          success: false,
          error: "Invalid patient ID",
          message: "Cannot update patient: Invalid ID",
        };
      }

      const cleanData = preparePatientData(patientData);
      console.log("📤 Updating patient ID:", patientId);
      console.log("📤 Update data:", cleanData);

      const response = await api.put(`/v1/patients/${patientId}`, cleanData);

      console.log("✅ Patient updated successfully:", response.data);

      return {
        success: true,
        data: response.data,
        message: "Patient profile updated successfully",
      };
    } catch (error) {
      console.error("❌ Error updating patient:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Request URL:", error.config?.url);

      const fieldErrors = parseBackendErrors(error.response?.data);

      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to update patient profile",
        fieldErrors,
      };
    }
  },

  getAllPatients: async () => {
    try {
      const response = await api.get("/v1/patients");
      return {
        success: true,
        data: response.data,
        message: "All patients loaded successfully",
      };
    } catch (error) {
      console.error("Error loading all patients:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to load patients",
        data: [],
      };
    }
  },

  deletePatient: async (patientId) => {
    try {
      await api.delete(`/v1/patients/${patientId}`);
      return {
        success: true,
        message: "Patient deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting patient:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to delete patient",
      };
    }
  },

  getMedicalHistory: async (patientId) => {
    try {
      const response = await api.get(`/v1/patients/${patientId}/medical-history`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching medical history:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: null,
      };
    }
  },

  updateMedicalHistory: async (patientId, medicalHistory) => {
    try {
      const response = await api.put(
        `/v1/patients/${patientId}/medical-history`,
        medicalHistory
      );
      return {
        success: true,
        data: response.data,
        message: "Medical history updated successfully",
      };
    } catch (error) {
      console.error("Error updating medical history:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to update medical history",
      };
    }
  },

  updateConsents: async (patientId, consents) => {
    try {
      const response = await api.put(
        `/v1/patients/${patientId}/consents`,
        consents
      );
      return {
        success: true,
        data: response.data,
        message: "Consents updated successfully",
      };
    } catch (error) {
      console.error("Error updating consents:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to update consents",
      };
    }
  },

  getProfileCompletion: async (patientId) => {
    try {
      const response = await api.get(`/v1/patients/${patientId}/completion`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching profile completion:", error);
      return {
        success: false,
        data: { completionPercentage: 0 },
      };
    }
  },

  getSavedTrials: async (patientId) => {
    try {
      const response = await api.get(`/v1/patients/${patientId}/saved-trials`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching saved trials:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: [],
      };
    }
  },

  searchPatients: async (filters) => {
    try {
      const params = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key].trim() !== "") {
          params[key] = filters[key].trim();
        }
      });

      console.log("🔍 Searching patients with params:", params);

      const response = await api.get("/v1/patients/search", {
        params,
      });

      return {
        success: true,
        data: response.data,
        message: "Patients loaded successfully",
      };
    } catch (error) {
      console.error("❌ Error searching patients:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to search patients",
        data: [],
      };
    }
  },

  getCurrentUserPatient: async () => {
    try {
      console.log('🔍 Getting current user patient profile...');
      const response = await api.get('/v1/patients/me');
      console.log('✅ Got current user patient:', response.data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Error getting current user patient:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get patient profile',
        data: null,
      };
    }
  },

  // ===== APPOINTMENTS =====

  getAppointments: async (patientId) => {
    try {
      const response = await api.get(`/v1/patients/${patientId}/appointments`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error fetching appointments:', error);
      return { success: false, error: error.response?.data?.error || error.message, data: [] };
    }
  },

  getNextAppointment: async (patientId) => {
    try {
      const response = await api.get(`/v1/patients/${patientId}/appointments/next`);
      // 204 No Content when nothing is scheduled — response.data is empty/undefined
      return { success: true, data: response.data || null };
    } catch (error) {
      console.error('❌ Error fetching next appointment:', error);
      return { success: false, error: error.response?.data?.error || error.message, data: null };
    }
  },

  getTodaysAppointments: async (patientId) => {
    try {
      const response = await api.get(`/v1/patients/${patientId}/appointments/today`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error fetching today\'s appointments:', error);
      return { success: false, error: error.response?.data?.error || error.message, data: [] };
    }
  },

  createAppointment: async (patientId, appointmentData) => {
    try {
      const response = await api.post(`/v1/patients/${patientId}/appointments`, appointmentData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error creating appointment:', error);
      return { success: false, error: error.response?.data?.error || error.message, data: null };
    }
  },

  updateAppointment: async (patientId, appointmentId, appointmentData) => {
    try {
      const response = await api.put(`/v1/patients/${patientId}/appointments/${appointmentId}`, appointmentData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error updating appointment:', error);
      return { success: false, error: error.response?.data?.error || error.message, data: null };
    }
  },

  deleteAppointment: async (patientId, appointmentId) => {
    try {
      await api.delete(`/v1/patients/${patientId}/appointments/${appointmentId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting appointment:', error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  },
};

export default patientService;